<template>
  <div class="row poppins h-100">
    <div class="col-12 h-100">
      <div class="table-wrapper h-100 d-flex flex-column">
        <div class="filter-block d-flex justify-content-end">
          <div class="d-flex">
            <b-dropdown
              v-if="!isGeneratingPdf"
              variant="primary"
              right
              :text="'Generate PDF' | i18n"
              size="lg"
              toggle-class="pl-4 pr-4"
              class="ml-3"
              :disabled="!selectedList.length"
            >
              <b-dropdown-item @click="() => generatePDF({ type: STICKER_TYPES.TEXT })">
                {{ 'Generate text PDF' | i18n }}
              </b-dropdown-item>
              <b-dropdown-item @click="() => generatePDF({ type: STICKER_TYPES.BARCODE })">
                {{ 'Generate barcode PDF' | i18n }}
              </b-dropdown-item>
            </b-dropdown>
            <b-button v-else variant="primary" size="lg" class="ml-3 pl-3 pr-4 d-flex align-items-center" disabled>
              <b-spinner v-if="isGeneratingPdf" small class="mr-3" />
              {{ $translate('Generating') }}
            </b-button>
            <b-button
              variant="primary"
              size="lg"
              class="ml-3 pl-3 pr-4 d-flex align-items-center"
              :disabled="isUpdatingCaseList || !this.selectedIdList.length || !isChanged"
              @click="updateCaseList"
            >
              <b-spinner v-if="isUpdatingCaseList" small class="mr-3" />
              {{ isUpdatingCaseList ? $translate('Updating...') : $translate('Update case list') }}
            </b-button>
          </div>
        </div>
        <div class="row">
          <div class="col-12 col-lg-6 col-sm-12 col-xs-12">
            <Input v-model="name" form-group-id="caseListName" :placeholder="'Name' | i18n" required />
          </div>
          <div v-if="unchangedSelectedIdList.length" class="col-12 col-lg-6 col-sm-12 col-xs-12">
            <Multiselect
              :options="generalCaseList.map(({ id, title, caseNumberString }) => ({ value: id, text: `${title} (№ - ${caseNumberString})` }))"
              form-group-id="caseIds"
              placeholder="Cases"
              :placeholder-input="'Select case' | i18n"
              :on-change="(v) => (selectedIdList = v)"
              required
              :init-values="selectedIdList"
            />
          </div>
        </div>
        <Table
          :update="getPage"
          :columns="columns"
          :rows="rows"
          :filter="filter"
          :updateFilter="updateFilter"
          :loadingInstance="loadingInstance"
          :setLoadingInstance="setLoadingInstance"
          :selectedList="selectedList"
          :setSelectedList="setSelectedList"
          class="overflow-auto pr-1"
          tableName="caseListDetailsTable"
        />
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';

import { ReportsMixin, DeleteRowMixin, NotificationMixin, AuthMixin } from '../../../../mixins';
import Table from '../../common/table';
import Input from '../../common/input';
import Multiselect from '../../common/multiselect';
import SearchInput from '../../common/search-input';
import { CASE_STATUSES, STICKER_TYPES } from '../../../../../../../constants';
import api from '../../../api';

export default {
  title: 'CaseListDetails',
  components: { Multiselect, Table, SearchInput, Input },
  mixins: [ReportsMixin, DeleteRowMixin, NotificationMixin, AuthMixin],
  data() {
    const generateCustomFilter = (columnKey) => {
      return {
        defaultVisible: false,
        render: ({ showFn, closeFn }, h) => {
          return (
            <div class="custom-name-filter">
              <input
                value={this.filter.searchData[columnKey]}
                on-input={(e) => (this.filter.searchData[columnKey] = e.target.value)}
                placeholder={this.$translate('Search value')}
              />
              <div class="custom-name-filter-operation">
                <span class="name-filter-cancel" on-click={() => closeFn()}>
                  {this.$translate('Cancel')}
                </span>
                <span class="name-filter-confirm" on-click={() => this.searchConfirm(closeFn)}>
                  {this.$translate('Search')}
                </span>
              </div>
            </div>
          );
        },
        filterIcon: () => {
          return <ve-icon name="search" />;
        },
      };
    };

    return {
      STICKER_TYPES,
      id: '',
      selectedList: [],
      loadingInstance: null,
      localBasePath: axios.defaults.baseURL,
      accessToken: '',
      filter: {
        page: 1,
        limit: 10,
        totalCount: 0,
        sortBy: 'createdAt',
        sortOrder: 'desc',
        searchData: {
          caseNumber: '',
          teamName: '',
          title: '',
          template: '',
          userName: '',
        },
      },
      columns: [
        {
          field: 'status',
          key: 'status',
          title: '',
          width: 0,
          renderBodyCell: ({ row, column }) => {
            const cellData = row[column.field];
            const color = (() => {
              switch (cellData) {
                case CASE_STATUSES.ACTIVE:
                  return 'active';
                case CASE_STATUSES.IN_PROGRESS:
                  return 'inprogress';
                case CASE_STATUSES.FINISHED:
                  return 'finished';
              }
            })();
            return <div class={`status-cell ${color}-status`} />;
          },
        },
        {
          field: 'check',
          key: 'check',
          type: 'checkbox',
          title: '',
          width: 50,
          align: 'center',
        },
        {
          field: 'caseNumber',
          title: '#',
          width: 100,
          key: 'caseNumber',
          sortBy: '',
          align: 'left',
          renderBodyCell: ({ row }, h) => row.caseNumberString,
          filterCustom: generateCustomFilter('caseNumberString'),
        },
        {
          field: 'teamName',
          title: this.$translate('team'),
          key: 'teamName',
          align: 'left',
          filterCustom: generateCustomFilter('team.name'),
        },
        {
          field: 'title',
          title: this.$translate('title'),
          key: 'title',
          align: 'left',
          sortBy: '',
          filterCustom: generateCustomFilter('title'),
        },
        {
          field: 'template.name',
          title: this.$translate('template'),
          key: 'template.name',
          align: 'left',
          sortBy: '',
          renderBodyCell: ({ row }, h) => row.templateTitle,
          filterCustom: generateCustomFilter('template.name'),
        },
        {
          field: 'userName',
          title: this.$translate('Assigned User'),
          key: 'userName',
          width: 200,
          filterCustom: generateCustomFilter('user.username'),
        },
        {
          field: 'createdAt',
          title: this.$translate('Date Created'),
          key: 'createdAt',
          sortBy: 'desc',
          width: 200,
        },
      ],
      rows: [],
      generalCaseList: [],
      name: '',
      selectedIdList: [],
      unchangedName: '',
      unchangedSelectedIdList: [],
      isGeneratingPdf: false,
      isUpdatingCaseList: false,
    };
  },
  computed: {
    isChanged() {
      const isCaseListLengthNotEqual = this.selectedIdList.length !== this.unchangedSelectedIdList.length;
      const isCaseListChanged = this.selectedIdList.some(({ value }) => !this.unchangedSelectedIdList.includes(value));
      return this.name !== this.unchangedName || isCaseListChanged || isCaseListLengthNotEqual;
    },
  },
  async created() {
    this.id = this.$route.params.id;

    const auth = this.authStorageService.getAuth();
    this.accessToken = auth.access_token_link;

    const generalCaseList = await this.reportsService.getCasesPage({});
    this.generalCaseList = generalCaseList;
  },
  methods: {
    updateFilter(f) {
      this.filter = {
        ...this.filter,
        ...f,
      };
    },
    setSelectedList(list) {
      this.selectedList = list;
    },
    setLoadingInstance(loadingInstance) {
      this.loadingInstance = loadingInstance;
    },
    searchConfirm(closeFn) {
      this.getPage();
      closeFn();
    },
    startLoading() {
      this.loadingInstance.show();
    },
    stopLoading() {
      this.loadingInstance.close();
    },
    async getPage() {
      try {
        this.startLoading();
        const { page, limit, sortBy, sortOrder, searchData } = this.filter;
        const params = {
          page,
          limit,
          sortBy,
          sortOrder,
          searchData,
        };
        const response = await api.caseLists.getById(this.id, params);
        response.data.casesList = response.data.casesList.map((item) => ({
          ...item,
          teamName: item.team.name,
          createdAt: moment(item.createdAt).format('DD.MM.YY HH:mm'),
          templateTitle: item.template.name,
          userName: item.user.name,
        }));

        this.filter.totalCount = response.data.totalCount;
        this.name = response.data.data.name;
        this.unchangedName = response.data.data.name;
        this.rows = response.data.casesList;
        this.selectedIdList = response.data.data.idList.map(({ id, title, caseNumberString }) => ({
          value: id,
          text: `${title} (№ - ${caseNumberString})`,
        }));
        this.unchangedSelectedIdList = response.data.data.idList.map(({ id }) => id);
        this.stopLoading();
      } catch (err) {
        this.showNotification({ type: 'error', message: err.message });
      }
    },
    async updateCaseList() {
      try {
        this.isUpdatingCaseList = true;
        const data = {
          name: this.name,
          idList: this.selectedIdList.map(({ value }) => value),
        };
        await api.caseLists.update(this.id, data);
        this.showNotification({ type: 'success', message: 'Case list has successfully updated!' });
        await this.getPage();
      } catch (error) {
        this.showNotification({ type: 'error', message: error.message });
      } finally {
        this.isUpdatingCaseList = false;
      }
    },
    async generatePDF({ type }) {
      try {
        this.isGeneratingPdf = true;
        const { data } = await api.sticker.generatePdf({ idList: this.selectedList.map(({ id }) => id), type });
        if (data.pdfName) {
          const url = `${this.localBasePath}/api/v1/reports/stickers-download-pdf/${data.pdfName}?authorization=${this.accessToken}`;
          await axios(`${url}&delete=false`);
          window.open(`${url}&delete=true`, '_blank');
        }
        this.showNotification({ type: 'success', message: this.$translate('PDF was successfully downloaded!') });
      } catch (error) {
        this.showNotification({ type: 'error', message: this.$translate('Generate pdf error') });
      } finally {
        this.isGeneratingPdf = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
::v-deep .ve-table-body {
  ::v-deep th {
    color: rgb(112, 112, 112) !important;
    background-color: rgb(239, 239, 239) !important;
    border: none !important;
    min-height: 50px;
  }
  ::v-deep td {
    color: rgb(112, 112, 112) !important;
    cursor: pointer;
  }
}

::v-deep .ve-table-cell-selection {
  border: none !important;
}

::v-deep .ve-table-sort {
  top: -4.5px;
}

::v-deep .ve-dropdown-dt {
  z-index: 45 !important;
}

::v-deep .status-cell {
  position: absolute;
  top: 0;
  left: 0;
  width: 5px;
  height: 100%;
  border-radius: 0 2px 2px 0;
}
::v-deep .active-status {
  background-color: rgb(37, 154, 43);
}
::v-deep .inprogress-status {
  background-color: rgb(239, 255, 15);
}
::v-deep .finished-status {
  background-color: rgb(90, 98, 107);
}

.pagination-block {
  display: flex;
  justify-content: flex-end;
  margin-top: 30px;
}

::v-deep .btn-lg {
  font-size: 16px;
  line-height: 24px;
}

::v-deep .size-12 {
  font-size: 12px;
  line-height: 12px;
}
</style>
